import React, { Fragment } from 'react';
import { Container, Spinner } from 'react-bootstrap';
import Accordion from 'react-bootstrap/Accordion';
import './ProjectItemPage.css';
import { Radio } from 'antd';
import { useWorkPreparation } from '../../../../../workPreparation';
import ProjectActionButtons from './ProjectActionButtons';
import WeatherProtectionAccordionItems from './WeatherProtectionAccordionItems';
import ScaffoldingAccordionItem from './ScaffoldingAccordionItem';
import SelfControlAccordionItem from './SelfControlAccordionItem';
import RiskAssesmentAccordionItem from './RiskAssesmentAccordionItem';
import RescuePlanAccordionItem from './RescuePlanAccordionItem';
import PlanForConductAccordionItem from './PlanForConductAccordionItem';
import DimensionDocumentsAccordionItem from './DimensionDocumentsAccordionItem';
import MonteringsanalysAccordionItem from './MonteringsanalysAccordionItem';
import TableOfContentsSection from './TableOfContentsSection';
import PreStartCheckAccordionItems from './PreStartCheckAccordionItems';

interface ProjectItemPageProps {
  ProjectId: number;
}

const ProjectItemPage: React.FC<ProjectItemPageProps> = ({ ProjectId }) => {
  const {
    loading,
    notFound,
    workerView,
    managementView,
    hasMonteringsanalys,
    hasBothAccesses,
    userRole,
    setUserRole,
    sections,
    signScaffoldingSelfControl,
    signWeatherProtectionSelfControl,
    save,
    endProject,
    generateDocuments,
    print,
    disableAllExceptPrint,
  } = useWorkPreparation(ProjectId);

  if (notFound) {
    return (
      <Container className="text-center mt-5">
        <h3>Projektet kunde inte hittas.</h3>
        <p>Kontrollera projekt-ID i URL:en eller gå tillbaka till projektlistan.</p>
      </Container>
    );
  }

  if (loading) {
    return (
      <div className='d-flex align-items-center justify-content-center' style={{ minHeight: '50vh' }}>
        <Spinner animation="border" variant="warning" className='me-2'></Spinner>
        Laddar projektdata...
      </div>
    );
  }

  if (sections.selfControl.error) { return <div>Error loading self control container: {sections.selfControl.error.message}</div> };
  if (sections.scaffolding.error) { return <div>Error loading factAboutScaffolding: {sections.scaffolding.error.message}</div> };
  if (sections.weatherProtection.weatherProtectionFactsError) { return <div>Error load factaboutweatherprotection {sections.weatherProtection.weatherProtectionFactsError.message}</div> }
  if (sections.weatherProtection.error) { return <div>Error load selfcontrolweatherprotection {sections.weatherProtection.error.message}</div> }
  if (sections.rescuePlan.error) { return <div>Error load selfcontrolweatherprotection {sections.rescuePlan.error.message}</div> }
  if (sections.planForConduct.error) { return <div>Error load planforconduct {sections.planForConduct.error.message}</div> }
  if (sections.riskAssessment.error) { return <div>Error load riskassesment {sections.riskAssessment.error.message}</div> }
  if (sections.monteringsanalys.error) { return <div>Error load monteringsanalys {sections.monteringsanalys.error.message}</div> }

  return (
    <Container className='accordion-wrapper'>
      <div className="project-role-toggle-wrapper d-flex justify-content-between align-items-center flex-wrap gap-1">
        {hasBothAccesses && (
          <div className="ms-auto">
            <Radio.Group
              className="project-role-toggle"
              value={userRole}
              onChange={(e) => setUserRole(e.target.value)}
            >
              <Radio.Button value="management">Arbetsledarvy</Radio.Button>
              <Radio.Button value="worker">Montörsvy</Radio.Button>
            </Radio.Group>
          </div>
        )}
      </div>
      <TableOfContentsSection
        projectInfo={sections.tableOfContents.projectInfo}
        tableOfContentData={sections.tableOfContents.tableOfContentData}
        workerView={workerView}
      />

      <Accordion className='mt-5'>
        <Fragment>
          <PreStartCheckAccordionItems
            workerView={workerView}
            projectId={sections.preStartCheck.projectId}
            isWeatherProtectionProject={sections.preStartCheck.isWeatherProtectionProject}
          />
          <WeatherProtectionAccordionItems
            projectId={sections.weatherProtection.projectId}
            factsAboutWeatherProtectionData={sections.weatherProtection.factsAboutWeatherProtectionData}
            factsAboutWeatherProtectionCompletionStatus={sections.weatherProtection.factsAboutWeatherProtectionCompletionStatus}
            setFactsAboutWeatherProtectionData={sections.weatherProtection.setFactsAboutWeatherProtectionData}
            selfControlWeatherProtectionData={sections.weatherProtection.selfControlWeatherProtectionData}
            selfControlWeatherProtectionCompletionStatus={sections.weatherProtection.selfControlWeatherProtectionCompletionStatus}
            setSelfControlWeatherProtectionData={sections.weatherProtection.setSelfControlWeatherProtectionData}
            workerView={workerView}
            onSign={signWeatherProtectionSelfControl}
          />
          <ScaffoldingAccordionItem
            factsAboutScaffoldingData={sections.scaffolding.factsAboutScaffoldingData}
            setFactsAboutScaffoldingData={sections.scaffolding.setFactsAboutScaffoldingData}
            factAboutScaffoldingCompletionStatus={sections.scaffolding.factAboutScaffoldingCompletionStatus}
            workerView={workerView}
          />
          <SelfControlAccordionItem
            projectId={sections.selfControl.projectId}
            selfControlContainerData={sections.selfControl.selfControlContainerData}
            setSelfControlContainerData={sections.selfControl.setSelfControlContainerData}
            selfControlCompletionStatus={sections.selfControl.selfControlCompletionStatus}
            onSign={signScaffoldingSelfControl}
          />

          {hasMonteringsanalys && (
            <MonteringsanalysAccordionItem
              monteringsanalysData={sections.monteringsanalys.monteringsanalysData}
              projectInfo={sections.monteringsanalys.projectInfo}
              tableOfContentData={sections.monteringsanalys.tableOfContentData}
              workerView={workerView}
              managementView={managementView}
              onSave={sections.monteringsanalys.onSave}
              projectId={sections.monteringsanalys.projectId}
              onSign={sections.monteringsanalys.onSign}
              available={sections.monteringsanalys.available}
              completionRule={sections.monteringsanalys.completionRule}
            />
          )}

          <RiskAssesmentAccordionItem
            riskAssesmentContainerData={sections.riskAssessment.riskAssesmentContainerData}
            riskAssesmentCompletionStatus={sections.riskAssessment.riskAssesmentCompletionStatus}
            workerView={workerView}
            setRiskAssesmentContainerData={sections.riskAssessment.setRiskAssesmentContainerData}
            onApprove={sections.riskAssessment.onApprove}
          />
          <RescuePlanAccordionItem
            rescueplanData={sections.rescuePlan.rescueplanData}
            setRescueplanData={sections.rescuePlan.setRescueplanData}
            rescuePlanCompletionStatus={sections.rescuePlan.rescuePlanCompletionStatus}
            workerView={workerView}
            onApprove={sections.rescuePlan.onApprove}
          />
          <PlanForConductAccordionItem
            planForConductData={sections.planForConduct.planForConductData}
            setPlanForConductData={sections.planForConduct.setPlanForConductData}
            planForConductCompletionStatus={sections.planForConduct.planForConductCompletionStatus}
            workerView={workerView}
            isWeatherProtectionProject={sections.planForConduct.isWeatherProtectionProject}
            onApprove={sections.planForConduct.onApprove}
          />
          <DimensionDocumentsAccordionItem
            projectInfo={sections.dimensionDocuments.projectInfo}
            dimensionDocUpdated={sections.dimensionDocuments.dimensionDocUpdated}
            setDimensionDocUpdated={sections.dimensionDocuments.setDimensionDocUpdated}
            workerView={workerView}
          />
        </Fragment>
      </Accordion>
      <ProjectActionButtons
        isLoading={false}
        handleEndProject={endProject}
        handlePrint={print}
        handleUpdate={save}
        handleUploadToSharepoint={generateDocuments}
        managementView={managementView}
        disableAllExceptPrint={disableAllExceptPrint}
      />
    </Container>
  );
};

export default ProjectItemPage;
